/**
 * ChatHeads — Facebook Messenger-style floating chat heads for new DMs.
 *
 * When a new chat message arrives (unread count for a peer goes UP), a small
 * circular avatar slides in at the bottom-right. Click → expands into a mini
 * chat window (compact version of ChatPanel) so the user can reply without
 * leaving their current page. Multiple heads stack vertically.
 *
 * Lives ABOVE TaskToast (z-[115]) and the main ChatPanel (z-[100]).
 *
 * State machine per peer:
 *   - 'head'   : circle avatar (compact)
 *   - 'window' : expanded mini chat (320×420)
 *   - closed   : removed from heads array
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Maximize2 } from 'lucide-react';
import { useChat } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/AuthContext';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5_000;
const MAX_HEADS = 4;

const ChatHeads = () => {
  const { user } = useAuth();
  const { unreadByPeer, openPanel, refreshUnread } = useChat();
  const [heads, setHeads] = useState([]); // [{ peerId, peer, mode: 'head'|'window' }]
  const prevUnreadRef = useRef(null);  // null = uninitialised; first non-null poll seeds baseline

  // Single effect that compares each unreadByPeer snapshot against the previous one.
  // The very first snapshot is treated as the baseline (no toasts fire for it),
  // so existing unread messages don't blast chat heads on page load.
  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = { ...unreadByPeer };
    if (prev === null) return undefined; // first run = baseline
    const grew = [];
    Object.entries(unreadByPeer).forEach(([peerId, count]) => {
      if (count > (prev[peerId] || 0)) grew.push(peerId);
    });
    if (grew.length === 0) return undefined;
    grew.forEach(async (peerId) => {
      if (heads.some((h) => h.peerId === peerId)) return;
      try {
        const r = await axios.get(`${API}/chat/conversations/${peerId}`);
        const peer = r.data?.peer;
        setHeads((curr) => {
          if (curr.some((h) => h.peerId === peerId)) return curr;
          // Cap the stack so bubbles never overflow the viewport.
          return [...curr, { peerId, peer, mode: 'head' }].slice(-MAX_HEADS);
        });
      } catch (err) {
        // Peer may have been deleted or network is down — non-critical.
        log.debug('[chathead] failed to fetch peer', peerId, err?.message || err);
      }
    });
    return undefined;
  }, [unreadByPeer, heads]);

  const close = (peerId) => setHeads((curr) => curr.filter((h) => h.peerId !== peerId));
  const expand = (peerId) => setHeads((curr) => curr.map((h) => h.peerId === peerId ? { ...h, mode: 'window' } : h));
  const collapse = (peerId) => setHeads((curr) => curr.map((h) => h.peerId === peerId ? { ...h, mode: 'head' } : h));
  const popOut = (peerId) => {
    close(peerId);
    openPanel(peerId);
  };

  if (!user || heads.length === 0) return null;

  return (
    <div
      data-testid="chat-heads-stack"
      className="fixed bottom-20 right-6 z-[115] flex flex-col-reverse gap-3 items-end pointer-events-none"
      style={{ maxWidth: 'calc(100vw - 3rem)' }}
    >
      {heads.map((h) => (
        <div key={h.peerId} className="pointer-events-auto">
          {h.mode === 'head'
            ? <Head head={h} onExpand={() => expand(h.peerId)} onClose={() => close(h.peerId)} unread={unreadByPeer[h.peerId] || 0} />
            : <MiniWindow head={h} onCollapse={() => collapse(h.peerId)} onPopOut={() => popOut(h.peerId)} onClose={() => close(h.peerId)} refreshUnread={refreshUnread} />}
        </div>
      ))}
    </div>
  );
};

// ---------- Avatar bubble ----------
const Head = ({ head, onExpand, onClose, unread }) => {
  const initial = (head.peer?.name || '?').charAt(0).toUpperCase();
  return (
    <div className="relative group">
      <button
        onClick={onExpand}
        title={head.peer?.name}
        data-testid={`chat-head-${head.peerId}`}
        className="h-14 w-14 rounded-full bg-primary text-white text-lg font-bold shadow-2xl ring-4 ring-white grid place-items-center hover:scale-105 transition-transform"
        style={{ animation: 'chat-head-in 350ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {initial}
      </button>
      {unread > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center pointer-events-none">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
      <button
        onClick={onClose}
        aria-label="Dismiss"
        data-testid={`chat-head-${head.peerId}-close`}
        className="absolute -top-2 -left-2 h-5 w-5 rounded-full bg-white border border-mute/40 text-ink/60 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

// ---------- Expanded mini chat window ----------
const MiniWindow = ({ head, onCollapse, onPopOut, onClose, refreshUnread }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/conversations/${head.peerId}`);
      setMessages(r.data.messages || []);
      await axios.post(`${API}/chat/read/${head.peerId}`).catch(() => {});
      refreshUnread();
    } catch (err) {
      log.debug('[chathead] conversation load failed', err?.message || err);
    }
  }, [head.peerId, refreshUnread]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const handleSend = async () => {
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/chat/send`, { peer_id: head.peerId, content });
      setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
      setDraft('');
      load();
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid={`chat-window-${head.peerId}`}
      className="w-[320px] h-[420px] bg-white rounded-2xl shadow-2xl border border-mute/40 flex flex-col overflow-hidden"
      style={{ animation: 'chat-window-in 250ms cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-mute/30 flex items-center gap-2 bg-gradient-to-r from-primary/5 to-transparent">
        <div className="h-8 w-8 rounded-full bg-primary text-white text-sm font-bold grid place-items-center shrink-0">
          {(head.peer?.name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm text-ink truncate" data-testid={`chat-window-${head.peerId}-name`}>
            {head.peer?.name || 'Loading…'}
          </div>
          <div className="text-[10px] text-emerald-600 font-semibold">● Active</div>
        </div>
        <button
          onClick={onPopOut}
          title="Open in full panel"
          className="h-7 w-7 grid place-items-center rounded-full text-ink/50 hover:text-ink hover:bg-surface-100"
          data-testid={`chat-window-${head.peerId}-popout`}
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCollapse}
          title="Minimise"
          className="h-7 w-7 grid place-items-center rounded-full text-ink/50 hover:text-ink hover:bg-surface-100 text-xl leading-none pb-1"
        >
          –
        </button>
        <button
          onClick={onClose}
          title="Close"
          className="h-7 w-7 grid place-items-center rounded-full text-ink/50 hover:text-red-500 hover:bg-red-50"
          data-testid={`chat-window-${head.peerId}-close`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-surface-50">
        {messages.length === 0 && (
          <div className="text-center text-xs text-ink/40 py-8">No messages yet</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-sm ${mine ? 'bg-primary text-white rounded-br-sm' : 'bg-white text-ink rounded-bl-sm border border-mute/30'}`}>
                {m.content}
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="p-2 border-t border-mute/30 flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Aa"
          rows={1}
          className="flex-1 resize-none rounded-2xl border border-mute/40 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 max-h-20"
          data-testid={`chat-window-${head.peerId}-input`}
        />
        <button
          onClick={handleSend}
          disabled={sending || !draft.trim()}
          className="h-8 w-8 rounded-full bg-primary text-white grid place-items-center disabled:opacity-40 hover:bg-primary-hover shrink-0"
          data-testid={`chat-window-${head.peerId}-send`}
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
};

export default ChatHeads;
